import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { CreditNoteStatus } from '~/api/creditNotes/types';

import { useCreditNotes, useDownloadCreditNoteFcFiles } from '~/api/creditNotes';
import { HttpNotFoundError } from '~/api/errors';
import { QueryKeys } from '~/api/keys';
import { Button, ErrorState, useTable } from '~/components';
import { ColumnIds } from '~/constants/table';
import { useDebouncedState } from '~/hooks/useDebouncedState';
import { toast } from '~/utils/toast';

import { useCreditNotesTableColumns, usePrefetchNextPage } from './CreditNotesTable.hooks';
import {
  useCreditNotesTableColumnFilters,
  useCreditNotesTableGlobalFilter,
  useCreditNotesTablePaginationState,
  useCreditNotesTableSorting,
} from './CreditNotesTable.nuqs';

export const CreditNotesTable = () => {
  const [pagination, setPagination] = useCreditNotesTablePaginationState();
  const [sorting, setSorting] = useCreditNotesTableSorting();
  const [globalFilter, setGlobalFilter] = useCreditNotesTableGlobalFilter();
  const [columnFilters, setColumnFilters] = useCreditNotesTableColumnFilters();

  const [debouncedGlobalFilter] = useDebouncedState(globalFilter, 200);
  const queryMetadata = useMemo(
    () => ({
      pagination,
      sorting,
      filters: {
        search: debouncedGlobalFilter.trim(),
        clientId: columnFilters.find((filter) => filter.id === ColumnIds.creditNotesClient)?.value ?? null,
        brandId: columnFilters.find((filter) => filter.id === ColumnIds.creditNotesBrand)?.value ?? null,
        year: columnFilters.find((filter) => filter.id === ColumnIds.creditNotesDocYear)?.value ?? null,
        quarter: columnFilters.find((filter) => filter.id === ColumnIds.creditNotesDocQuarter)?.value ?? null,
        status: (columnFilters.find((filter) => filter.id === ColumnIds.creditNotesStatus)?.value ?? null) as CreditNoteStatus[] | null,
      },
    }),
    [debouncedGlobalFilter, columnFilters, pagination, sorting],
  );

  const { data, isError, isPending, isPlaceholderData } = useCreditNotes(queryMetadata);
  const queryClient = useQueryClient();

  usePrefetchNextPage(data?.pagination.nextPage ?? null, queryMetadata);

  const downloadCreditNotesMutation = useDownloadCreditNoteFcFiles();

  const { t } = useTranslation(['common', 'creditnotes', 'filters']);

  const columns = useCreditNotesTableColumns();
  const [table, Table] = useTable({
    data: data?.data ?? [],
    columns,
    initialState: {
      columnVisibility: {
        [ColumnIds.creditNotesBrand]: false,
        [ColumnIds.creditNotesDocYear]: false,
        [ColumnIds.creditNotesDocQuarter]: false,
      },
    },
    state: { pagination, sorting, globalFilter, columnFilters },
    enableRowSelection: (row) => row.original.status === 'Approved',
    enableSortingRemoval: false,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onGlobalFilterChange: (...args) => {
      setGlobalFilter(...args);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    onColumnFiltersChange: (...args) => {
      setColumnFilters(...args);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    rowCount: data?.pagination.totalResults,
  });

  if (isError) {
    return (
      <ErrorState
        onRetry={() => {
          queryClient.resetQueries({ queryKey: QueryKeys.creditNotes.index.metadata(queryMetadata) });
          setPagination(null);
          setSorting(null);
          setGlobalFilter(null);
          setColumnFilters(null);
        }}
        title={t('creditnotes:overview.error.title')}
      />
    );
  }

  return (
    <Table.Root table={table}>
      <Table.Menu>
        <Table.Search />

        <Table.Filters>
          <Table.Filter columnId={ColumnIds.creditNotesClient} type="combobox" />
          <Table.Filter columnId={ColumnIds.creditNotesBrand} hideWhen="singleOption" type="select" />
          <Table.Filter columnId={ColumnIds.creditNotesDocYear} type="multiselect" />
          <Table.Filter columnId={ColumnIds.creditNotesDocQuarter} type="multiselect" />
          <Table.Filter columnId={ColumnIds.creditNotesStatus} type="multiselect" />
        </Table.Filters>
      </Table.Menu>

      <Table.Grid isLoading={isPending || isPlaceholderData}>
        <Table.Header />

        <Table.Body>
          {(row) => <Table.Row onClick={row.original.status === 'Approved' ? () => row.toggleSelected() : undefined} row={row} />}
        </Table.Body>
      </Table.Grid>

      <Table.Pagination showPageSizeOptions />

      <Table.Selection>
        {(selectedRowIds) => (
          <Button
            loading={downloadCreditNotesMutation.isPending}
            onClick={() =>
              downloadCreditNotesMutation.mutate(
                { ids: selectedRowIds },
                {
                  onError: (error, { ids }) =>
                    toast.danger({
                      description:
                        error instanceof HttpNotFoundError
                          ? t('creditnotes:alerts.missingFcFiles', { count: ids.length })
                          : t('common:error'),
                    }),
                },
              )
            }
          >
            {`${t('common:datatable.download')} (${selectedRowIds.length})`}
          </Button>
        )}
      </Table.Selection>
    </Table.Root>
  );
};
